import type { AgentDeal, AgentDealUpdateRequest } from '@repo/database/src/types';

export interface ReferralData {
  id: string;
  alias: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  countryCode?: string | null;
  /** Player tier level (0=Bronze, 1=Silver, 2=Gold, 3=Platinum, 4=Phenom) */
  tierLevel?: number | null;

  agentDeals: AgentDeal[];
  rakeShareUpdates: AgentDealUpdateRequest[];

  /** Number of referrals who have been funded (deposited or received a transfer) */
  fundedReferralCount: number;
  totalReferralCount: number;

  totalRakeShare: number;
  directRakeShare?: number;
  /** Rake share from the referral's downline (totalRakeShare - directRakeShare) */
  downlineRakeShare?: number;
  cashRakeShare: number;
  tournamentRakeShare: number;

  /** Total rake generated by this referral (direct + downline) */
  totalRakeGenerated: number;
  /** Rake generated from this referral's own play */
  directRakeGenerated?: number;
  /** Rake generated from this referral's downline (if they're an agent with referrals) */
  downlineRakeGenerated?: number;

  /**
   * The rake share percentage of whoever called getUserAgentDealsV2 (the "referrer" in context).
   *
   * This value comes from the same `referral_rakeshare_aggregates` rows as the rake data,
   * ensuring self-consistency: if no aggregate data exists for the period, both this
   * percentage and the rake values will be 0 - there's nothing to calculate.
   *
   * Context-dependent usage:
   * - **Main table**: Represents the logged-in master agent's percentage
   * - **Sub-table**: When fetched from sub-agent's perspective, represents the sub-agent's
   *   percentage (used to calculate master agent's override earnings)
   */
  periodRakeSharePercentage?: number;

  walletBalance: number;
  referrerCode: string;
  smartWalletAddress?: string | null;

  isAgent: boolean;

  createdAt: string;
  lastActiveAt?: string;
}

export interface AgentDashboardResponse {
  players: ReferralData[];
  agents: ReferralData[];
}

export interface Period {
  startDate: string;
  endDate: string;
  amount: number;
}

/**
 * Period filter for analytics aggregation.
 * - 'weeks': Individual weeks
 * - 'fourWeeks': Rolling 4-week windows
 * - 'fiftyTwoWeeks': Rolling 52-week windows
 */
export type AnalyticsPeriodFilter = 'weeks' | 'fourWeeks' | 'fiftyTwoWeeks';

/**
 * Chart data point for weekly analytics.
 * Charts display 7 weeks (current period) vs 7 weeks (comparison period).
 */
export interface WeeklyChartDataPoint {
  /** Week label (e.g., "W1", "W2", or date range) */
  name: string;
  /** Value for current period (weeks 1-7), null if week hasn't occurred yet */
  currentPeriod: number | null;
  /** Value for comparison period (weeks 8-14) */
  comparisonPeriod: number;
  /** The current week number (1-7, where 1 = most recent completed week) */
  currentWeekNum: number;
  /** The comparison week number (8-14, where 8 compares to week 1) */
  comparisonWeekNum: number;
  /** Date range for current week (e.g., "Jan 6 - Jan 12") */
  currentWeekRange: string;
  /** Date range for comparison week (e.g., "Nov 11 - Nov 17") */
  comparisonWeekRange: string;
}

export interface AgentAnalytics {
  // Agent basic info
  alias: string;
  fullName: string;
  percentageDeal: number;
  countryCode: string;

  // Rake share data
  rakeShare: {
    total: number;
    /** Rake share from the previous completed week */
    lastWeek: number;
    /** Rake share from the last 4 completed weeks */
    lastFourWeeks: number;
    /** Weekly chart data: 7 weeks (current) vs 7 weeks (comparison) */
    chartData: WeeklyChartDataPoint[];
  };

  // Player statistics
  playerCount: {
    total: number;
    /** New players from the previous completed week */
    lastWeek: number;
    /** New players from the last 4 completed weeks */
    lastFourWeeks: number;
    totalDeposited: number;
    /** Players who played in the previous completed week */
    playedLastWeek: number;
    /** Players who played in the last 4 completed weeks */
    playedLastFourWeeks: number;
    /** Weekly chart data: 7 weeks (current) vs 7 weeks (comparison) */
    chartData: WeeklyChartDataPoint[];
  };

  // Rake share by period table data
  rakeShareByPeriod: {
    periods: Period[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
  };
}

export interface MasterAgentGlobalStats {
  totalRevenueShare: number;
  totalTokensEarned: number;
  totalReferrals: number;
  activeReferrals: number;
  dayRevenue?: {
    current: number;
    trend: number;
  };
  currentWeekRevenue: {
    current: number;
    trend: number;
  };
  lastWeekRevenue: {
    current: number;
    trend: number;
  };
  yearRevenue: {
    current: number;
    trend: number;
  };
}

export interface ReferralProgramTotals {
  totalRevenueShare: number;
  totalTokensEarned: number;
  totalReferrals: number;
  activeReferrals: number;
}

export interface MasterAgentStats {
  revShare: {
    total: number;
    thisWeek: number;
    lastWeek: number;
    lastFourWeeks: number;
    twoWeeksAgo: number;
    previousFourWeeks: number;
  };
  totalPlayers: number;
  totalAgents: number;
  directPlayers: number;
  directAgents: number;
  newPlayers: {
    lastWeek: number;
    lastMonth: number;
  };
}
